import { Component, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from "@angular/core";
import { BehaviorSubject, Subscription, switchMap } from "rxjs";
import { ApiService } from "@app/services/api/api.service";
import { VesselGroupsService } from "@app/services/vessel-groups/vessel-groups.service";
import { ReportsPanelComponent } from "@app/components/reports/reports-panel/reports-panel.component";
import { CiiService } from "@app/services/cii/cii.service";
import { CiiRanges } from "@app/services/api/api.models";
import { DecimalPipe } from "@angular/common";

interface InputCiiRange {
  rating: "A" | "B" | "C" | "D" | "E";
  minAer_gCO2_per_tnm: number;
  maxAer_gCO2_per_tnm?: number;
}

@Component({
  selector: "app-cii-rating",
  templateUrl: "./cii-rating.component.html",
  styleUrls: ["./cii-rating.component.scss"],
})
export class CiiRatingComponent implements OnChanges, OnInit, OnDestroy {
  @ViewChild(ReportsPanelComponent, { static: true }) reportsPanelComponent: ReportsPanelComponent;

  private subscriptions: Subscription[] = [];

  selectedVessel: any;

  ciiRating: string;
  aer_gtnm: number;
  ciiRangesCurrentYear: CiiRanges;

  ciiTracks: any | undefined;
  ciiRanges: any | undefined;
  ciiReport: any | undefined;

  availableHistoryYears: number[] = [];
  selectedYear$ = new BehaviorSubject<number>(new Date().getFullYear());

  constructor(
    private apiService: ApiService,
    private vesselGroupsService: VesselGroupsService,
    private ciiService: CiiService,
    private decimalPipe: DecimalPipe
  ) {}

  ngOnInit() {
    this.apiService
      .initialize()
      .toPromise()
      .then(() => {
        this.subscriptions.push(
          this.vesselGroupsService.getSelectedVesselObservable().subscribe({
            next: (selectedVessel) => {
              this.selectedVessel = selectedVessel;
            },
          }),
          this.ciiService.getCiiReportObservable().subscribe({
            next: (ciiReport) => {
              this.processCiiReport(ciiReport);
            },
          }),
          this.selectedYear$.pipe(switchMap((year) => this.ciiService.getCiiTracksObservable(year))).subscribe({
            next: (ciiTracks) => {
              this.processCiiTracks(ciiTracks);
            },
          })
        );
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["selectedVessel"]) {
      this.processCiiReport(this.ciiReport);
    }
    if (changes["ciiReport"]) {
      this.processCiiReport(this.ciiReport);
    }
  }

  ngOnDestroy() {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  getSelectedVesselId(): number {
    return this.vesselGroupsService.getSelectedVesselId();
  }

  getVesselName() {
    return this.selectedVessel ? this.selectedVessel.name : "";
  }

  onYearSelect(year: number) {
    this.selectedYear$.next(year);
  }

  valueOrNV(value: number, format: string) {
    return value != null ? this.decimalPipe.transform(value, format) : "NV";
  }

  private buildRanges(ranges: InputCiiRange[]): CiiRanges {
    if (!ranges) {
      return null;
    }
    const averageRangeSize = (ranges[3].maxAer_gCO2_per_tnm! - ranges[0].minAer_gCO2_per_tnm) / 4;

    let eMax = ranges[3].maxAer_gCO2_per_tnm! + averageRangeSize;

    const getRange = (rating: string): [number, number] => {
      const r = ranges.find((rr) => rr.rating === rating)!;
      return [r.minAer_gCO2_per_tnm, r.maxAer_gCO2_per_tnm ?? Number.POSITIVE_INFINITY];
    };
    return {
      a: getRange("A"),
      b: getRange("B"),
      c: getRange("C"),
      d: getRange("D"),
      e: [getRange("D")[1], Number(eMax.toFixed(2))],
    };
  }

  private processCiiTracks(ciiTracks) {
    if (ciiTracks === undefined) {
      this.ciiTracks = undefined;
    } else {
      this.ciiRanges = this.buildRanges(ciiTracks.ciiRanges);
      this.ciiTracks = ciiTracks.history;
    }
  }

  private processCiiReport(ciiReport) {
    this.ciiReport = ciiReport;
    if (ciiReport === undefined) {
      this.ciiRating = undefined;
      this.ciiRangesCurrentYear = undefined;
      this.aer_gtnm = undefined;
      this.availableHistoryYears = undefined;
      return;
    }
    this.ciiRating = ciiReport?.ciiRating || null;
    this.ciiRangesCurrentYear = this.buildRanges(ciiReport?.ciiRanges || null);
    this.aer_gtnm = ciiReport?.currentAer_gCO2_per_tnm || null;
    this.availableHistoryYears = ciiReport?.availableHistoryYears || null;

    const currentYear = new Date().getFullYear();

    if (!this.availableHistoryYears.includes(currentYear)) {
      this.availableHistoryYears.push(currentYear);
    }
  }
}
